import React, {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useContext,
} from 'react';
import AuthContext from '../context/AuthContext';
import { useGraphData } from '../hooks/useGraphData';
import { useDrag } from '../hooks/useDrag';
import { usePinAnchors } from '../hooks/usePinAnchors';
import { useAskAI } from '../hooks/useAskAI';
import { GraphNodes } from './GraphNodes';
import { GraphEdges } from './GraphEdges';
import { createNode } from '../api/createNode';
import { deleteNode } from '../api/deleteNode';
import { MdModeStandby } from 'react-icons/md';
import { PiLinktreeLogoBold } from 'react-icons/pi';
import { RiAddBoxLine } from 'react-icons/ri';
import './ContainerGraph.css';

export default function ContainerGraph() {
  const { authFetch } = useContext(AuthContext);
  const { nodes, setNodes, edges, setEdges, nodeDepthMap } = useGraphData(0);
  const containerRef = useRef(null);
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const [hoveredLinks, setHoveredLinks] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [linkMode, setLinkMode] = useState(false);
  const [linkedNodes, setLinkedNodes] = useState([]);
  const [storedInputValue, setStoredInputValue] = useState('');
  const [popupNodeId, setPopupNodeId] = useState(null);
  const handleMouseDown = useDrag(nodes, setNodes);
  const [selectedEdge, setSelectedEdge] = useState(null);

  const pinAnchors = usePinAnchors(containerRef, [nodes, selectedNodeId]);

  const linkModeEdge =
    linkMode && linkedNodes.length === 2
      ? [
          {
            source: linkedNodes[0].id,
            target: linkedNodes[1].id,
            isTemporary: true,
          },
        ]
      : [];

  const {
    askAI: handleAskAI,
    isAskingAI,
    popupValue,
    setPopupValue,
  } = useAskAI();

  const handleCreateNode = async () => {
    try {
      const newNode = await createNode(authFetch, {
        x_axis: 33,
        y_axis: 33,
        label: 'Title',
        text_content: 'Contents',
      });

      setNodes((prev) => [...prev, newNode]);

      console.log('Node sent to backend');
    } catch (error) {
      console.error('Error creating node:', error);
    }
  };

  useEffect(() => {
    if (selectedEdge) {
      setLinkedNodes([]);
      setPopupNodeId(null);
      setPopupValue('');
    }
  }, [selectedEdge]);

  // Click-outside handling
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) {
        setSelectedNodeId(null);
      }
    };
    window.addEventListener('mousedown', handleClickOutside);
    return () => window.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="graph-container" ref={containerRef}>
      <div className="panel">
        <button
          className={`panel-buttons ${linkMode ? 'panel-buttons-active' : ''}`}
          onClick={() => {
            setLinkMode((prev) => !prev);
            setLinkedNodes([]);
            setPopupNodeId(null);
            setPopupValue('');
            setSelectedEdge(null);
          }}
        >
          {linkMode ? (
            <PiLinktreeLogoBold size={25} />
          ) : (
            <PiLinktreeLogoBold size={25} />
          )}
        </button>

        <button className="panel-buttons" onClick={handleCreateNode}>
          <RiAddBoxLine size={25} />
        </button>
      </div>
      {/* HTML: nodes layer, ABOVE edges */}
      <GraphNodes
        nodes={nodes}
        setNodes={setNodes}
        edges={edges}
        setEdges={setEdges}
        hoveredNodeId={hoveredNodeId}
        setHoveredNodeId={setHoveredNodeId}
        hoveredLinks={hoveredLinks}
        setHoveredLinks={setHoveredLinks}
        selectedNodeId={selectedNodeId}
        setSelectedNodeId={setSelectedNodeId}
        handleMouseDown={handleMouseDown}
        linkedNodes={linkedNodes}
        setLinkedNodes={setLinkedNodes}
        popupNodeId={popupNodeId}
        setPopupNodeId={setPopupNodeId}
        popupValue={popupValue}
        setPopupValue={setPopupValue}
        setStoredInputValue={setStoredInputValue}
        isAskingAI={isAskingAI}
        handleAskAI={() => handleAskAI(linkedNodes)}
        linkMode={linkMode}
        selectedEdge={selectedEdge} // <-- new
        setSelectedEdge={setSelectedEdge} // <-- optional, if needed for deletion
      />

      {/* SVG: edges layer, BELOW nodes */}
      <GraphEdges
        nodes={nodes}
        edges={[...edges, ...linkModeEdge]}
        hoveredNodeId={hoveredNodeId}
        anchors={pinAnchors}
        linkMode={linkMode}
        onEdgeClick={setSelectedEdge}
      />
    </div>
  );
}
